import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import Header from "./components/header";
import Footer from "./components/footer";
import News from "./components/news";
import Info from "./components/info";
import ArticleLoader from "./components/ArticleLoader";
import PagesLoader from "./components/PagesLoader";

const API = (process.env.REACT_APP_API_BASE_URL || "http://localhost:7000").replace(/\/$/, "");
const WEBSITE_ID = process.env.REACT_APP_WEBSITE_ID;

export default function App() {
  const [website, setWebsite] = useState(null);
  const [sections, setSections] = useState([]); // [{_id,name,slug,type,about}, ...]
  const [itemsBySection, setItemsBySection] = useState({}); // { sectionId: [items] }
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (!WEBSITE_ID) throw new Error("Missing REACT_APP_WEBSITE_ID");

        // 1) Public website meta + sections
        const [wRes, scRes] = await Promise.all([
          fetch(`${API}/api/public/websites/${WEBSITE_ID}/meta`),
          fetch(`${API}/api/public/site-contents/by-website/${WEBSITE_ID}`)
        ]);
        if (!wRes.ok) throw new Error("Failed loading website");
        const w = await wRes.json();
        setWebsite(w);

        if (!scRes.ok) throw new Error("Failed loading site contents");
        const sec = await scRes.json();
        setSections(Array.isArray(sec) ? sec : []);

        // 2) For each section, load its items (public)
        const pairs = await Promise.all(
          (sec || []).map(async s => {
            try {
              const r = await fetch(`${API}/api/public/content-items/by-content/${s._id}`);
              if (!r.ok) return [s._id, []];
              const list = await r.json();
              return [s._id, Array.isArray(list) ? list : []];
            } catch {
              return [s._id, []];
            }
          })
        );
        setItemsBySection(Object.fromEntries(pairs));
      } catch (e) {
        setErr(e.message || "Failed to initialize");
      }
    })();
  }, []);

  // Build articles (from section 'nyheter') and pages (from every other section)
  const { articles, pages } = useMemo(() => {
    const outArticles = [];
    const outPages = [];

    for (const s of sections || []) {
      const items = itemsBySection[s._id] || [];
      const sectionSlug = (s.slug || "").toLowerCase();

      for (const it of items) {
        const title = it.name;
        const slug  = it.link;
        const html  = it.content || "";
        if (!title || !slug) continue;

        if (sectionSlug === "nyheter") {
          // News articles live under /nyheter/:slug
          outArticles.push({
            title,
            slug,
            content: html,
            section: s
          });
        } else {
          // Every other section becomes namespaced pages: /:sectionSlug/:itemSlug
          outPages.push({
            title,
            slug,
            content: html,
            section: s // keep reference to build route path
          });
        }
      }
    }
    return { articles: outArticles, pages: outPages };
  }, [sections, itemsBySection]);

  if (err) return <div className="p-16 text-red-600">Fel: {err}</div>;
  if (!website) return <div className="p-16">Laddar in...</div>;

  return (
    <Router>
      <Header website={website} sections={sections} itemsBySection={itemsBySection} />

      <Routes>
        {/* News listing */}
        <Route path="/nyheter" element={<News data={articles} />} />

        {/* Static info (keep as-is) */}
        <Route path="/foreningsinfo" element={<Info />} />

        {/* Article detail: /nyheter/:slug */}
        {articles.map(a => (
          <Route
            key={`art-${a.slug}`}
            path={`/nyheter/${a.slug}`}
            element={
              <ArticleLoader
                sectionSlug="nyheter"
                itemSlug={a.slug}
                fallbackTitle={a.title}
              />
            }
          />
        ))}

        {/* Pages by section and item: /:sectionSlug/:itemSlug (e.g., /lankar/stadgar) */}
        {pages.map(p => (
          <Route
            key={`page-${p.section._id}-${p.slug}`}
            path={`/${p.section.slug}/${p.slug}`}
            element={
              <PagesLoader
                sectionSlug={p.section.slug}
                itemSlug={p.slug}
                fallbackTitle={p.title}
              />
            }
          />
        ))}

        {/* Default → /nyheter */}
        <Route path="/" element={<Navigate to="/nyheter" replace />} />
      </Routes>

      <Footer />
    </Router>
  );
}
